import {Card, ColorSchemeType} from 'platform/components';
import {Scroll, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil} from 'ramda';

import {CheckoutMessageLogEntryResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {getCheckoutMessageOrderSummary} from '../utils/getCheckoutMessageOrderSummary';
import {shortenId} from '../utils/shortenId';

interface CheckoutMessageLogEntryProps extends TestIdProps {
  entry: CheckoutMessageLogEntryResponseBody;
}

const getStatusColorScheme = (
  status: CheckoutMessageLogEntryResponseBody['status']
): ColorSchemeType =>
  match(status)
    .with('failed', always('red' as const))
    .with('ignored', always('yellow' as const))
    .with('produced', 'received', always('green' as const))
    .exhaustive();

const getStatusLabel = (status: CheckoutMessageLogEntryResponseBody['status']) =>
  match(status)
    .with('failed', always(i18n.t('general.notifications.labels.failed')))
    .with('ignored', always(i18n.t('entity.checkout.labels.messageStatusIgnored')))
    .with('produced', always(i18n.t('entity.checkout.labels.messageStatusProduced')))
    .with('received', always(i18n.t('general.labels.received')))
    .exhaustive();

const getDirectionLabel = (direction: CheckoutMessageLogEntryResponseBody['direction']) =>
  match(direction)
    .with('outbound', always(i18n.t('entity.checkout.labels.messageDirectionOutbound')))
    .with('inbound', always(i18n.t('entity.checkout.labels.messageDirectionInbound')))
    .exhaustive();

export function CheckoutMessageLogEntry(props: CheckoutMessageLogEntryProps) {
  const formatDateTime = useDateTimeFormatter();

  const orderSummary = getCheckoutMessageOrderSummary(props.entry.payload);

  const parameters = [
    props.entry.channel,
    props.entry.errorClass,
    isNotNil(orderSummary)
      ? i18n.t('entity.checkout.labels.messageLogOrderParameter', {
          orderId: shortenId(orderSummary.orderId),
          itemName: orderSummary.itemName,
        })
      : null,
    isNotNil(props.entry.transactionId)
      ? i18n.t('entity.checkout.labels.messageLogTransactionParameter', {
          transactionId: shortenId(props.entry.transactionId),
        })
      : null,
  ].filter(isNotNil);
  const statusColorScheme = getStatusColorScheme(props.entry.status);

  return (
    <Card
      title={props.entry.eventType ?? props.entry.messageClass ?? props.entry.id}
      subtitle={formatDateTime('dateTimeMedium', parseDate(props.entry.recordedAt))}
      colorStripe={statusColorScheme}
      parameters={parameters}
      flags={[
        {label: getDirectionLabel(props.entry.direction), colorScheme: 'neutral'},
        {label: getStatusLabel(props.entry.status), colorScheme: statusColorScheme},
      ]}
      isExpandable
      isClosedByDefault={props.entry.status !== 'failed'}
      data-testid={suffixTestId('entry', props)}
    >
      <Show when={props.entry.errorMessage}>
        <Text size="small" color="danger" data-testid={suffixTestId('entry-errorMessage', props)}>
          {props.entry.errorMessage}
        </Text>
      </Show>
      <Scroll auto maxHeight={80}>
        <pre data-testid={suffixTestId('entry-payload', props)}>
          {JSON.stringify(props.entry.payload, null, 2)}
        </pre>
      </Scroll>
    </Card>
  );
}
